import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, AlertCircle, TrendingUp, Calendar, PieChart, Shield, Users, FileText } from 'lucide-react';
import { MarketingHeader } from '../components/MarketingHeader';
import { MarketingFooter } from '../components/MarketingFooter';
import { SeoHead } from '../components/SeoHead';

export function GestionTresorerieAssociationPage() {
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const handleSignUp = () => {
    navigate('/login');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Quelle différence entre budget et trésorerie ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Le budget prévisionnel présente les recettes et dépenses attendues sur un exercice complet, donnant une vision annuelle équilibrée. La trésorerie suit le cash disponible à chaque instant, c'est-à-dire le montant exact présent sur vos comptes bancaires. Votre trésorerie peut être négative même si votre budget annuel est équilibré, simplement parce qu'une subvention tarde à arriver."
        }
      },
      {
        "@type": "Question",
        "name": "Comment anticiper un déficit de trésorerie ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Pour anticiper un déficit, établissez un plan de trésorerie mensuel détaillé intégrant les dates réelles d'encaissement des subventions et de décaissement des charges. Identifiez les mois à risque et prévoyez des solutions : report de dépenses non urgentes, demande d'avance de subvention, constitution d'une réserve de trésorerie, ou mise en place d'une ligne de trésorerie bancaire."
        }
      },
      {
        "@type": "Question",
        "name": "Quel outil utiliser pour gérer la trésorerie d'une association ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plusieurs options existent : tableur Excel (gratuit mais chronophage et sans automatisation), logiciel comptable (orienté comptabilité, pas pilotage), ou outil spécialisé comme TrezoOne qui offre un plan de trésorerie automatisé, le suivi des subventions, des simulations de scénarios et des alertes en temps réel."
        }
      },
      {
        "@type": "Question",
        "name": "Une association peut-elle avoir une trésorerie négative ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Juridiquement, une association peut avoir une trésorerie négative temporairement si elle dispose d'une autorisation de découvert bancaire. Cependant, c'est une situation à risque qu'il faut anticiper et éviter autant que possible. Les frais bancaires et le risque de refus de paiement des salaires rendent cette situation dangereuse pour la pérennité de l'association."
        }
      },
      {
        "@type": "Question",
        "name": "Comment gérer des subventions versées en plusieurs fois ?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Intégrez dans votre plan de trésorerie les dates prévisionnelles de chaque versement (acompte initial, versements intermédiaires sur justificatifs, solde final). Anticipez les décalages en constituant un fonds de roulement suffisant. Négociez avec les financeurs des calendriers de versement alignés sur vos besoins de trésorerie. Utilisez un outil de suivi des subventions pour suivre l'avancement de chaque justificatif et anticiper les encaissements."
        }
      }
    ]
  };

  return (
    <>
      <SeoHead
        title="Gestion trésorerie association : outil simple et prévisionnel"
        description="Outil de gestion de trésorerie pour association : plan prévisionnel, suivi subventions, alertes et simulation de trésorerie."
        keywords="gestion trésorerie association, outil gestion trésorerie association, logiciel gestion trésorerie association, plan de trésorerie association, prévisionnel de trésorerie association"
        canonical="/gestion-tresorerie-association"
        jsonLd={faqSchema}
      />

      <div className="min-h-screen bg-white">
        <MarketingHeader
          onNavigate={handleNavigate}
          onSignIn={handleSignIn}
          onSignUp={handleSignUp}
        />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 pt-24 pb-16 px-4">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Gestion de trésorerie pour association : méthode, outils et pilotage efficace
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              La <strong>gestion de trésorerie d'une association</strong> est un enjeu crucial pour assurer sa pérennité financière. Face aux retards de subventions, aux charges fixes incompressibles et aux décalages entre encaissements et décaissements, disposer d'un <strong>outil de gestion de trésorerie pour association</strong> performant devient indispensable. Découvrez comment anticiper vos besoins en cash, sécuriser vos décisions et piloter sereinement votre <strong>plan de trésorerie association</strong>.
            </p>
          </div>
        </section>

        {/* Section 1: Définition */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Qu'est-ce que la gestion de trésorerie d'une association ?
            </h2>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              La <strong>gestion de trésorerie</strong> consiste à suivre et anticiper les flux financiers réels de votre association : entrées et sorties d'argent, jour après jour, mois après mois.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              Budget vs Trésorerie : quelle différence ?
            </h3>

            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Le <strong>budget prévisionnel</strong> présente les recettes et dépenses attendues sur un exercice comptable complet. Il donne une vision annuelle équilibrée. Mais il ne dit pas <em>quand</em> l'argent entre ou sort réellement.
            </p>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              La <strong>trésorerie</strong>, elle, suit le cash disponible à chaque instant. C'est le montant exact présent sur vos comptes bancaires. Elle peut être négative même si votre budget annuel est équilibré, simplement parce qu'une subvention tarde à arriver.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 my-8">
              <p className="text-gray-800 font-medium">
                <strong>Exemple concret :</strong> Votre association prévoit 100 000 € de recettes et 100 000 € de dépenses sur l'année (budget équilibré). Mais si 60 000 € de subventions arrivent en juin alors que vous devez payer 8 salaires par mois dès janvier, vous serez en difficulté de trésorerie pendant 5 mois, même avec un budget parfaitement équilibré.
              </p>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              Spécificités des associations
            </h3>

            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Les associations font face à des contraintes financières particulières :
            </p>

            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700"><strong>Subventions versées en plusieurs fois</strong> : souvent après justification des dépenses, créant un décalage important</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700"><strong>Saisonnalité des recettes</strong> : adhésions en début d'année scolaire, dons en fin d'année fiscale</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700"><strong>Charges fixes incompressibles</strong> : salaires mensuels, loyers, assurances à honorer quoi qu'il arrive</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700"><strong>Ressources conditionnées</strong> : subventions affectées à des projets précis</span>
              </li>
            </ul>

            <h3 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              Risques liés aux retards de subventions
            </h3>

            <p className="text-lg text-gray-700 leading-relaxed">
              Un retard de versement peut mettre en péril toute l'activité de l'association : impossibilité de payer les salaires, reports de projets, perte de crédibilité auprès des partenaires. Le <strong>prévisionnel de trésorerie association</strong> permet justement d'anticiper ces tensions et de prendre les bonnes décisions au bon moment.
            </p>
          </div>
        </section>

        {/* Section 2: Problèmes fréquents */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Pourquoi la trésorerie est le point sensible des associations ?
            </h2>

            <div className="space-y-8">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="bg-red-100 p-3 rounded-lg">
                    <AlertCircle className="text-red-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Retard de subventions
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Les organismes publics versent souvent les subventions avec plusieurs mois de retard. Entre la notification, la signature de la convention, la fourniture des justificatifs et le versement effectif, il peut s'écouler 3 à 6 mois. Pendant ce temps, votre association doit continuer à fonctionner et payer ses charges.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <Users className="text-orange-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Charges fixes (salaires)
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Les salaires représentent souvent 60 à 80% du budget des associations employeuses. Ils doivent être payés chaque mois, sans possibilité de report. Un retard de salaire peut entraîner des sanctions pénales et détruire la confiance des équipes. C'est la contrainte de trésorerie la plus critique.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="bg-yellow-100 p-3 rounded-lg">
                    <Calendar className="text-yellow-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Décalage encaissements / décaissements
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Même avec un budget équilibré sur l'année, les recettes et dépenses ne sont pas synchronisées. Les adhésions arrivent souvent en septembre-octobre, les dons en décembre, mais les charges sont réparties sur 12 mois. Ce décalage temporel crée des périodes de tension sur la trésorerie.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <TrendingUp className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Manque de visibilité mensuelle
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      Sans outil adapté, impossible de savoir précisément combien vous aurez sur le compte dans 2, 3 ou 6 mois. Cette absence de visibilité empêche toute prise de décision éclairée : peut-on embaucher ? Investir dans du matériel ? Lancer un nouveau projet ? Un bon <strong>outil gestion trésorerie association</strong> répond à ces questions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Solutions */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Comment gérer la trésorerie d'une association efficacement ?
            </h2>

            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Plusieurs approches existent pour piloter votre trésorerie. Chacune présente des avantages et limites qu'il est important de connaître.
            </p>

            <div className="space-y-6">
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <FileText className="text-gray-600" size={24} />
                  Le tableur Excel ou Google Sheets
                </h3>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="font-semibold text-green-700 mb-2">Avantages :</p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Gratuit</li>
                      <li>• Flexible et personnalisable</li>
                      <li>• Pas de formation nécessaire</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-red-700 mb-2">Limites :</p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Risque d'erreurs de formule</li>
                      <li>• Chronophage (saisie manuelle)</li>
                      <li>• Pas d'alertes automatiques</li>
                      <li>• Pas de simulation de scénarios</li>
                    </ul>
                  </div>
                </div>
                <p className="text-gray-700 italic">
                  Adapté aux petites associations sans salariés avec peu de mouvements mensuels.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <PieChart className="text-gray-600" size={24} />
                  Le logiciel comptable classique
                </h3>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="font-semibold text-green-700 mb-2">Avantages :</p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Vision comptable complète</li>
                      <li>• Suivi des factures</li>
                      <li>• Connexion bancaire</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-red-700 mb-2">Limites :</p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Orienté comptabilité, pas pilotage</li>
                      <li>• Vision passée, pas prévisionnelle</li>
                      <li>• Complexe pour les non-comptables</li>
                      <li>• Pas de simulation de scénarios</li>
                    </ul>
                  </div>
                </div>
                <p className="text-gray-700 italic">
                  Indispensable pour la comptabilité, mais insuffisant pour le pilotage de trésorerie.
                </p>
              </div>

              <div className="border-2 border-blue-500 rounded-lg p-6 bg-blue-50">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <Shield className="text-blue-600" size={24} />
                  L'outil spécialisé gestion trésorerie association
                </h3>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="font-semibold text-green-700 mb-2">Avantages :</p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Vision prévisionnelle automatisée</li>
                      <li>• Alertes en temps réel</li>
                      <li>• Simulation de scénarios</li>
                      <li>• Suivi des subventions intégré</li>
                      <li>• Rapports pour CA/bureau</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-orange-700 mb-2">À considérer :</p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Coût mensuel ou annuel</li>
                      <li>• Temps de prise en main initial</li>
                    </ul>
                  </div>
                </div>
                <p className="text-gray-700 font-medium">
                  Solution recommandée pour les associations employeuses et/ou avec budget &gt; 100 000 €/an.
                </p>
              </div>
            </div>

            <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <p className="text-gray-800 leading-relaxed">
                <strong>Notre constat :</strong> Excel convient aux très petites structures, mais dès qu'une association emploie du personnel ou gère plusieurs subventions, un <strong>logiciel gestion trésorerie association</strong> dédié devient rapidement indispensable pour éviter les erreurs et gagner un temps précieux.
              </p>
            </div>
          </div>
        </section>

        {/* Section 4: Présentation outil TrezoOne */}
        <section className="py-16 px-4 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Un outil de gestion de trésorerie pour association conçu pour anticiper
            </h2>

            <p className="text-xl text-gray-700 mb-10 leading-relaxed">
              TrezoOne est un <strong>outil gestion trésorerie association</strong> pensé spécifiquement pour les besoins des structures associatives employeuses. Il transforme votre pilotage financier en automatisant les tâches chronophages et en vous donnant une visibilité claire sur les mois à venir.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Calendar className="text-blue-600" size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Plan de trésorerie mensuel automatisé
                </h3>
                <p className="text-gray-700">
                  Visualisez votre trésorerie prévisionnelle sur 12 mois avec mise à jour automatique à chaque nouvelle opération. Anticipez les mois difficiles et sécurisez vos décisions.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="text-green-600" size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Suivi des subventions
                </h3>
                <p className="text-gray-700">
                  Gérez vos subventions de A à Z : notification, convention, versements échelonnés, justificatifs. Recevez des alertes pour ne jamais manquer une échéance.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="bg-orange-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="text-orange-600" size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Simulation de retard de subvention
                </h3>
                <p className="text-gray-700">
                  Testez l'impact d'un retard de paiement sur votre trésorerie. Identifiez le moment où vous passerez en négatif et préparez un plan B.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="bg-red-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <AlertCircle className="text-red-600" size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Alertes automatiques
                </h3>
                <p className="text-gray-700">
                  Recevez des notifications lorsque votre trésorerie approche du seuil de sécurité. Réagissez à temps, pas dans l'urgence.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <PieChart className="text-purple-600" size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Indicateur fonds de roulement (FDR)
                </h3>
                <p className="text-gray-700">
                  Calculez automatiquement votre capacité à absorber les décalages de trésorerie. Connaissez votre marge de sécurité réelle.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="text-blue-600" size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  Rapports pour conseil d'administration
                </h3>
                <p className="text-gray-700">
                  Générez en un clic des rapports clairs et pédagogiques pour présenter la situation financière à vos instances de gouvernance.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-sm border-2 border-blue-500">
              <p className="text-lg text-gray-800 leading-relaxed mb-4">
                <strong>Résultat concret :</strong> Nos utilisateurs gagnent en moyenne 5 heures par mois sur le pilotage financier et détectent les tensions de trésorerie 2 à 3 mois à l'avance, contre seulement quelques jours avec Excel.
              </p>
              <p className="text-gray-700 italic">
                "Avant TrezoOne, je passais 2 jours par mois sur Excel à recalculer tout manuellement. Aujourd'hui, j'ai une vision claire en temps réel et je peux me concentrer sur le développement de nos actions." — Marie D., Directrice association 15 salariés
              </p>
            </div>
          </div>
        </section>

        {/* Section 5: Cas concrets */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Cas pratiques de gestion de trésorerie en association
            </h2>

            <div className="space-y-8">
              <div className="bg-gray-50 rounded-lg p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Cas n°1 : Retard de subvention de 30 000 €
                </h3>

                <div className="mb-6">
                  <p className="font-semibold text-gray-900 mb-2">Contexte :</p>
                  <p className="text-gray-700">
                    Association d'insertion avec 8 salariés. Masse salariale : 25 000 €/mois. Subvention départementale de 30 000 € prévue en mars, finalement versée en juin (3 mois de retard).
                  </p>
                </div>

                <div className="mb-6">
                  <p className="font-semibold text-gray-900 mb-2">Impact trésorerie :</p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• <strong>Mars :</strong> Trésorerie initiale 20 000 € - charges 25 000 € = -5 000 € (découvert)</li>
                    <li>• <strong>Avril :</strong> -5 000 € - 25 000 € = -30 000 € (découvert aggravé)</li>
                    <li>• <strong>Mai :</strong> -30 000 € - 25 000 € = -55 000 € (situation critique)</li>
                    <li>• <strong>Juin :</strong> -55 000 € + 30 000 € subvention - 25 000 € = -50 000 €</li>
                  </ul>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-red-800 font-semibold">
                    Sans anticipation : découvert bancaire non autorisé, risque de rejet des virements salaires, frais bancaires élevés.
                  </p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="font-semibold text-green-900 mb-2">Avec TrezoOne :</p>
                  <ul className="space-y-2 text-green-800">
                    <li>• Détection du problème dès janvier (simulation intégrée)</li>
                    <li>• Demande d'avance de 30% auprès du financeur négociée en février</li>
                    <li>• Report de 2 recrutements prévus</li>
                    <li>• Activation d'une ligne de crédit de trésorerie en prévention</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Cas n°2 : Embauche d'un salarié
                </h3>

                <div className="mb-6">
                  <p className="font-semibold text-gray-900 mb-2">Contexte :</p>
                  <p className="text-gray-700">
                    Association culturelle, trésorerie actuelle 45 000 €. Projet d'embauche d'un coordinateur à 2 800 €/mois (salaire + charges). Recettes mensuelles stables : 8 000 €, charges actuelles : 6 500 €/mois.
                  </p>
                </div>

                <div className="mb-6">
                  <p className="font-semibold text-gray-900 mb-2">Question :</p>
                  <p className="text-gray-700 italic">
                    L'association peut-elle embaucher sans risquer une rupture de trésorerie ?
                  </p>
                </div>

                <div className="mb-6">
                  <p className="font-semibold text-gray-900 mb-2">Analyse avec plan de trésorerie :</p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Nouvelles charges totales : 6 500 € + 2 800 € = 9 300 €/mois</li>
                    <li>• Recettes stables : 8 000 €/mois</li>
                    <li>• Déficit mensuel : -1 300 €/mois</li>
                    <li>• Autonomie : 45 000 € ÷ 1 300 € = 34 mois environ</li>
                  </ul>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                  <p className="text-orange-800 font-semibold">
                    Verdict : embauche techniquement possible MAIS nécessite de développer rapidement de nouvelles recettes pour compenser le déficit structurel. Risque à moyen terme.
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="font-semibold text-blue-900 mb-2">Recommandation TrezoOne :</p>
                  <p className="text-blue-800">
                    Conditionner l'embauche à l'obtention d'une nouvelle subvention de 15 000 €/an minimum ou au développement d'une activité générant +1 500 €/mois de recettes supplémentaires.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Cas n°3 : Investissement matériel
                </h3>

                <div className="mb-6">
                  <p className="font-semibold text-gray-900 mb-2">Contexte :</p>
                  <p className="text-gray-700">
                    Association sportive, projet d'achat d'un minibus : 35 000 €. Trésorerie actuelle : 50 000 €. Subvention Région confirmée : 20 000 € (versement prévu dans 4 mois). Charges mensuelles : 12 000 €, recettes mensuelles : 13 500 €.
                  </p>
                </div>

                <div className="mb-6">
                  <p className="font-semibold text-gray-900 mb-2">Simulation trésorerie :</p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• <strong>Mois 0 (achat immédiat) :</strong> 50 000 € - 35 000 € = 15 000 €</li>
                    <li>• <strong>Mois 1 :</strong> 15 000 € + 1 500 € = 16 500 €</li>
                    <li>• <strong>Mois 2 :</strong> 16 500 € + 1 500 € = 18 000 €</li>
                    <li>• <strong>Mois 3 :</strong> 18 000 € + 1 500 € = 19 500 €</li>
                    <li>• <strong>Mois 4 :</strong> 19 500 € + 1 500 € + 20 000 € subvention = 41 000 €</li>
                  </ul>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <p className="text-yellow-800 font-semibold">
                    Trésorerie minimale : 15 000 € (mois 0). Acceptable SI aucune dépense imprévue, MAIS marge de sécurité très faible pendant 4 mois.
                  </p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="font-semibold text-green-900 mb-2">Solution optimale :</p>
                  <ul className="space-y-2 text-green-800">
                    <li>• Négocier un paiement en 2 fois : 50% à la commande, 50% à livraison (dans 2 mois)</li>
                    <li>• Ou attendre le versement de la subvention avant achat</li>
                    <li>• Constituer au préalable une réserve de sécurité supplémentaire de 10 000 €</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 6: Guide pratique */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Comment faire un plan de trésorerie pour une association ?
            </h2>

            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Créer un <strong>plan de trésorerie association</strong> efficace suit une méthodologie précise en 5 étapes. Voici comment procéder, que vous utilisiez Excel ou un outil dédié.
            </p>

            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-blue-500">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-600 text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Lister toutes les charges fixes mensuelles
                    </h3>
                    <p className="text-gray-700 mb-3">
                      Recensez exhaustivement vos dépenses récurrentes incompressibles :
                    </p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Salaires bruts et charges patronales</li>
                      <li>• Loyer et charges locatives</li>
                      <li>• Assurances (RC, multirisques, etc.)</li>
                      <li>• Abonnements (téléphone, internet, logiciels)</li>
                      <li>• Remboursements de prêts éventuels</li>
                    </ul>
                    <p className="text-gray-700 mt-3 italic">
                      Ces charges sont prévisibles au centime près et doivent être honorées chaque mois.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-green-500">
                <div className="flex items-start gap-4">
                  <div className="bg-green-600 text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Lister toutes les recettes prévues
                    </h3>
                    <p className="text-gray-700 mb-3">
                      Identifiez vos sources de revenus et leurs montants :
                    </p>
                    <ul className="space-y-1 text-gray-700">
                      <li>• Subventions (distinguez les tranches de versement)</li>
                      <li>• Adhésions et cotisations</li>
                      <li>• Prestations de service</li>
                      <li>• Dons et mécénat</li>
                      <li>• Produits d'activités annexes</li>
                    </ul>
                    <p className="text-gray-700 mt-3">
                      <strong>Attention :</strong> indiquez le montant réellement encaissable, pas le montant notifié. Une subvention de 50 000 € versée en 3 fois doit apparaître comme 3 lignes distinctes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-orange-500">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-600 text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Positionner les dates exactes d'encaissement et décaissement
                    </h3>
                    <p className="text-gray-700 mb-3">
                      C'est l'étape cruciale qui différencie le budget du plan de trésorerie. Pour chaque ligne :
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li>• <strong>Charges :</strong> date de prélèvement (ex : salaires le 28, loyer le 5)</li>
                      <li>• <strong>Recettes :</strong> date d'encaissement effectif (pas de promesse, du réel)</li>
                    </ul>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                      <p className="text-yellow-800 text-sm">
                        <strong>Conseil :</strong> Pour les subventions, ajoutez systématiquement 1 à 2 mois de marge de sécurité aux délais annoncés. Les retards sont fréquents.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-purple-500">
                <div className="flex items-start gap-4">
                  <div className="bg-purple-600 text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Calculer le solde mensuel et cumulé
                    </h3>
                    <p className="text-gray-700 mb-3">
                      Pour chaque mois, calculez :
                    </p>
                    <div className="bg-gray-50 rounded p-4 mb-3 font-mono text-sm">
                      <p className="text-gray-900"><strong>Solde du mois</strong> = Recettes du mois - Dépenses du mois</p>
                      <p className="text-gray-900 mt-2"><strong>Trésorerie fin de mois</strong> = Trésorerie début mois + Solde du mois</p>
                    </div>
                    <p className="text-gray-700">
                      La trésorerie fin de mois devient la trésorerie début du mois suivant. C'est un calcul en cascade sur 12 mois minimum.
                    </p>
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-red-800 font-semibold text-sm">
                        Si un mois affiche une trésorerie négative, c'est un signal d'alerte critique !
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-red-500">
                <div className="flex items-start gap-4">
                  <div className="bg-red-600 text-white font-bold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    5
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Déterminer le seuil minimal de sécurité
                    </h3>
                    <p className="text-gray-700 mb-3">
                      Ne vous contentez pas de rester positif. Définissez un <strong>seuil de trésorerie minimal</strong> en dessous duquel vous ne devez jamais descendre.
                    </p>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                      <p className="font-semibold text-blue-900 mb-2">Règle pratique :</p>
                      <p className="text-blue-800 text-sm">
                        Seuil minimal = 1 à 2 mois de charges fixes
                      </p>
                      <p className="text-blue-700 text-sm mt-2">
                        Exemple : si vos charges mensuelles sont de 20 000 €, votre seuil de sécurité devrait être de 20 000 € à 40 000 €.
                      </p>
                    </div>
                    <p className="text-gray-700">
                      Ce matelas de sécurité vous permet d'absorber un imprévu (retard de subvention, dépense urgente) sans basculer dans le rouge.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">
                Automatisez votre plan de trésorerie avec TrezoOne
              </h3>
              <p className="text-blue-100 mb-6 leading-relaxed">
                Ces 5 étapes sont indispensables, mais chronophages à mettre à jour manuellement chaque mois. TrezoOne les automatise et recalcule votre <strong>plan de trésorerie association</strong> en temps réel à chaque nouvelle opération.
              </p>
              <button
                onClick={() => navigate('/auth')}
                className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors inline-flex items-center gap-2"
              >
                Tester gratuitement pendant 14 jours
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </section>

        {/* Section 7: FAQ */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">
              Questions fréquentes sur la gestion de trésorerie d'une association
            </h2>

            <div className="space-y-6">
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Quelle différence entre budget et trésorerie ?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Le <strong>budget prévisionnel</strong> présente les recettes et dépenses attendues sur un exercice complet, donnant une vision annuelle équilibrée. La <strong>trésorerie</strong> suit le cash disponible à chaque instant, c'est-à-dire le montant exact présent sur vos comptes bancaires. Votre trésorerie peut être négative même si votre budget annuel est équilibré, simplement parce qu'une subvention tarde à arriver. Le budget répond à "combien ?", la trésorerie répond à "quand ?".
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Comment anticiper un déficit de trésorerie ?
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Pour anticiper un déficit, établissez un <strong>plan de trésorerie mensuel</strong> détaillé intégrant les dates réelles d'encaissement des subventions et de décaissement des charges. Identifiez les mois à risque et prévoyez des solutions en amont :
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Report de dépenses non urgentes</li>
                  <li>• Demande d'avance ou d'acompte auprès des financeurs</li>
                  <li>• Constitution d'une réserve de trésorerie (fonds de roulement)</li>
                  <li>• Mise en place d'une ligne de trésorerie bancaire négociée en amont</li>
                  <li>• Campagne de dons ou recherche de financement complémentaire</li>
                </ul>
                <p className="text-gray-700 mt-3">
                  Un bon <strong>outil gestion trésorerie association</strong> vous alerte 2 à 3 mois avant la zone critique.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6 bg-blue-50">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Quel outil utiliser pour gérer la trésorerie d'une association ?
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Plusieurs options existent selon la taille de votre structure :
                </p>
                <ul className="space-y-2 text-gray-700 mb-3">
                  <li>• <strong>Tableur Excel/Sheets</strong> : gratuit mais chronophage, adapté aux très petites structures sans salarié</li>
                  <li>• <strong>Logiciel comptable</strong> : nécessaire pour la comptabilité mais orienté passé, pas anticipation</li>
                  <li>• <strong>Outil spécialisé</strong> (TrezoOne, Axonaut, etc.) : automatise le plan de trésorerie, alertes, simulations</li>
                </ul>
                <p className="text-gray-700 font-medium">
                  Pour les associations employeuses ou gérant plusieurs subventions, un <strong>logiciel gestion trésorerie association</strong> dédié devient rapidement indispensable pour gagner du temps et éviter les erreurs.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Une association peut-elle avoir une trésorerie négative ?
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Juridiquement, une association peut avoir une trésorerie négative temporairement si elle dispose d'une autorisation de découvert bancaire négociée avec sa banque. Cependant, c'est une situation à risque qu'il faut anticiper et éviter autant que possible. Les frais bancaires (agios) et le risque de refus de paiement des salaires rendent cette situation dangereuse pour la pérennité de l'association. Une trésorerie structurellement négative révèle un problème de modèle économique à résoudre rapidement.
                </p>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Comment gérer des subventions versées en plusieurs fois ?
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Les subventions fractionnées nécessitent une gestion rigoureuse :
                </p>
                <ol className="space-y-2 text-gray-700 list-decimal list-inside mb-3">
                  <li><strong>Intégrez dans votre plan de trésorerie</strong> les dates prévisionnelles de chaque versement (acompte initial souvent 30-40%, versements intermédiaires sur justificatifs, solde final)</li>
                  <li><strong>Anticipez les décalages</strong> en constituant un fonds de roulement suffisant pour couvrir les dépenses en attendant les versements</li>
                  <li><strong>Négociez avec les financeurs</strong> des calendriers de versement alignés sur vos besoins réels de trésorerie</li>
                  <li><strong>Utilisez un outil de suivi</strong> pour tracker l'avancement de chaque justificatif et anticiper les encaissements</li>
                </ol>
                <p className="text-gray-700">
                  Le module de suivi des subventions de TrezoOne vous permet de gérer chaque tranche de versement et d'être alerté des échéances de justificatifs.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 8: CTA Final */}
        <section className="py-20 px-4 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">
              Pilotez la trésorerie de votre association avec visibilité
            </h2>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed max-w-3xl mx-auto">
              Automatisez votre plan de trésorerie, anticipez les tensions, simulez vos décisions et sécurisez l'avenir financier de votre association. TrezoOne est l'<strong>outil de gestion de trésorerie pour association</strong> qui vous fait gagner du temps et de la sérénité.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10">
              <button
                onClick={() => navigate('/auth')}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition-colors inline-flex items-center gap-2 shadow-lg"
              >
                Tester gratuitement 14 jours
                <ArrowRight size={24} />
              </button>
              <button
                onClick={() => navigate('/tarifs')}
                className="bg-blue-500 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-400 transition-colors border-2 border-white"
              >
                Voir les tarifs
              </button>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">14 jours</div>
                <div className="text-blue-200">d'essai gratuit</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">Sans CB</div>
                <div className="text-blue-200">aucun engagement</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold mb-2">5h/mois</div>
                <div className="text-blue-200">gagnées en moyenne</div>
              </div>
            </div>

            <p className="text-blue-200 mt-10 text-sm">
              Rejoignez les centaines d'associations qui pilotent sereinement leur trésorerie avec TrezoOne
            </p>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </>
  );
}
